import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import type { MenuItem } from 'primeng/api';
import { AuthService, DEMO_TENANT_NAME } from '@/app/auth/auth.service';
import { resolveRoleLanding } from '@/app/auth/role-landing';
import { DashboardPersonaService } from '@/app/pages/dashboard/dashboard-persona.service';
import { environment } from '../../environments/environment';

/**
 * Demo "acting role" switcher, rendered as a pill in the top header. Selecting a role
 * performs a REAL re-authentication — it logs in as that role's demo account (reusing the
 * normal login flow, which swaps the session cookie) and reloads the current page under the
 * new identity. This is not a client-side costume change: the backend then enforces that
 * identity's permissions on every request. Gated behind environment.demoRoleSwitcher —
 * never renders in production.
 */
// `label` is the demo-facing name shown in the switcher; `role` is the real DB role name used to
// match the current session and never renamed (some role names are hard-referenced elsewhere).
// Pharmacist Validator / Content Engineer / Approver are
// display aliases over existing roles; the rest are genuinely new demo roles + users.
const DEMO_ROLE_ACCOUNTS: ReadonlyArray<{ label: string; role: string; username: string; password: string }> = [
    { label: 'Content Engineer', role: 'Clinical Informatician', username: 'analyst', password: 'Analyst@123' },
    { label: 'Pharmacist Validator', role: 'Oncology Pharmacist', username: 'pharmacist', password: 'Pharm@123' },
    { label: 'Physician Validator', role: 'Physician Validator', username: 'physvalidator', password: 'PhysVal@123' },
    { label: 'Nurse Validator', role: 'Nurse Validator', username: 'nursevalidator', password: 'NurseVal@123' },
    { label: 'Approver', role: 'Clinical Approver', username: 'physician', password: 'Phys@123' },
    { label: 'Order Sets Analyst', role: 'Order Sets Analyst', username: 'ordersets', password: 'OrderSet@123' },
    { label: 'Executive', role: 'Executive', username: 'executive', password: 'Exec@123' },
    { label: 'Site Administrator', role: 'Site Administrator', username: 'hisadmin', password: 'HIS@Admin123' },
    { label: 'Platform Administrator', role: 'Platform Administrator', username: 'admin', password: 'Admin@123' }
];

@Component({
    selector: 'app-role-switcher',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MenuModule],
    template: `
        @if (enabled()) {
            <button
                type="button"
                class="role-pill"
                data-role-switcher
                [disabled]="switching()"
                aria-haspopup="true"
                aria-label="Switch acting role"
                (click)="roleMenu.toggle($event)"
            >
                <i class="pi pi-shield role-pill-icon"></i>
                <span class="role-pill-label">{{ currentRole() ?? 'Select role' }}</span>
                <i class="pi pi-chevron-down role-pill-chevron"></i>
            </button>
            <p-menu #roleMenu [model]="menuItems()" [popup]="true" appendTo="body" />
        }
    `,
    styles: `
        :host {
            display: inline-flex;
            align-items: center;
        }
        /* The global topbar theme forces every > a/button into a 2.5rem circle
           (border-radius: 50%); override the box model here so the pill keeps its shape. */
        .role-pill {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            width: auto !important;
            height: auto !important;
            min-width: 0 !important;
            padding: 6px 12px !important;
            border-radius: 20px !important;
            background: rgba(255, 255, 255, 0.14) !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            line-height: 1;
            transition: background-color 0.15s ease;
        }
        .role-pill:hover {
            background: rgba(255, 255, 255, 0.22) !important;
        }
        .role-pill:disabled {
            opacity: 0.6;
            cursor: default;
        }
        .role-pill-icon {
            font-size: 14px;
            color: #fff;
        }
        .role-pill-label {
            font-size: 12.5px;
            font-weight: 600;
            color: #fff;
            white-space: nowrap;
        }
        .role-pill-chevron {
            font-size: 11px;
            color: #fff;
        }
        /* Mark the current acting role in the dropdown list. The menu is appendTo="body"
           (rendered outside this component), so the rule must be global — no :host prefix. */
        ::ng-deep .role-item-active .p-menu-item-link {
            font-weight: 700;
            color: var(--primary-color);
        }
    `
})
export class RoleSwitcherComponent {
    private readonly auth = inject(AuthService);
    private readonly personaService = inject(DashboardPersonaService);

    readonly switching = signal(false);

    /** Only renders in demo builds, for a signed-in user. */
    readonly enabled = computed(() => environment.demoRoleSwitcher && !!this.auth.currentUser());

    /** The demo account the current session maps to, matched on the real DB role name. */
    private readonly currentAccount = computed(() => {
        const roles = this.auth.currentUser()?.roles ?? [];
        return DEMO_ROLE_ACCOUNTS.find((a) => roles.includes(a.role)) ?? null;
    });

    /** Demo-facing label shown in the pill (falls back to the raw role name for non-demo users).
     *  Explicitly `string | null` so the template's `?? 'Select role'` fallback is real (avoids NG8102). */
    readonly currentRole = computed<string | null>(() => {
        const roles = this.auth.currentUser()?.roles ?? [];
        return this.currentAccount()?.label ?? roles[0] ?? null;
    });

    /** Dropdown items — one per demo role (display label), the active one flagged; selecting re-authenticates. */
    readonly menuItems = computed<MenuItem[]>(() => {
        const current = this.currentAccount();
        return DEMO_ROLE_ACCOUNTS.map((account) => ({
            label: account.label,
            styleClass: account === current ? 'role-item-active' : undefined,
            command: () => this.switchTo(account)
        }));
    });

    switchTo(account: (typeof DEMO_ROLE_ACCOUNTS)[number]): void {
        if (account === this.currentAccount() || this.switching()) return;

        this.switching.set(true);
        // Real re-auth: log in as the demo account (swaps the session cookie), then land on
        // that role's home screen so the demo shows role-based landing clearly.
        this.auth.login(DEMO_TENANT_NAME, account.username, account.password).subscribe({
            next: (res) => {
                const landing = resolveRoleLanding(res.user.roles);
                this.personaService.setPersona(landing.persona);
                window.location.assign(landing.defaultRoute);
            },
            error: () => this.switching.set(false)
        });
    }
}
